import * as React from 'react';
import HistorySection from 'Components/HistorySection';
import HistoryRowListing from 'Components/HistoryRowListing';
import DocumentCreationButton from 'Components/DocumentCreationButton';
import HistoryRowControls from 'Components/HistoryRowControls';
import { Container, Item } from 'Components/Grid';
import InfoPopover from 'Components/InfoPopover';
import { useAppSelector } from 'Store/index';
import { isByInformant } from 'Utility/documentHandling';
import { LocalizerCtx } from 'Containers/Localizer';
import { fbimod } from 'neuro-schemas';
import { fm } from 'Components/FormatMessage';

const { calculateScore } = fbimod;

const FBIMODHistory = ({ documents, startEdit, handleMyDelete, view }: IOwnProps): React.JSX.Element => {
  const { locale } = React.useContext(LocalizerCtx);

  const localizations = fbimod.getLocalizations(locale ?? 'fi');
  const localize = (id: keyof typeof localizations): string => {
    const localized = localizations[id];
    // eslint-disable-next-line no-console
    if (!localized) console.error(`fbimod: couldn't localize id ${id}`);
    return localized ?? '';
  };

  const informants = useAppSelector((state: IState) => state.patient.informants) ?? [];

  return (
    <HistorySection
      headerText={localize('title')}
      newButton={
        <DocumentCreationButton
          name="fbimod"
          text={'diagnosticSurvey.fbimod.newFBIMOD'}
          onClick={startEdit({}, 'fbimod')}
          width={15}
          height={3}
          fontSize={14}
          alternate={true}
        />
      }
    >
      <HistoryRowListing
        documents={documents}
        headers={<Item xs={true}>{fm('diagnosticSurvey.fbimod.score')}</Item>}
        contentFormat={d => {
          const isByInf = isByInformant(d, informants);
          return (
            <Container>
              <Item xs={isByInf ? 2 : true}>
                {calculateScore(d) ? (
                  fm('diagnosticSurvey.fbimod.scoreTotal', { value: calculateScore(d) as number })
                ) : (
                  <InfoPopover text="general.notFilled" color="primary" />
                )}
              </Item>
              {isByInf ? <Item xs={true}>{fm('diagnosticSurvey.fbimod.byInformant')}</Item> : undefined}
            </Container>
          );
        }}
        makeDate
        makeControls
        historyRowControls={d => {
          const isByInf = isByInformant(d, informants);
          return (
            <HistoryRowControls
              document={d}
              startEdit={startEdit}
              view={view}
              enableButtons={isByInf}
              deleteHandler={isByInf ? () => handleMyDelete(d._id, d._type) : undefined}
              hideEditHistory={isByInf}
            />
          );
        }}
      />
    </HistorySection>
  );
};

interface IOwnProps {
  documents: IDocumentWithTypeV2<'fbimod'>[];
  startEdit: StartEdit<IDocumentWithTypeV2<'fbimod'>>;
  handleMyDelete: (myDocId: string, docType: TMyDocumentType) => Promise<void>;
  view?: IView;
}

export default FBIMODHistory;
